import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BellRinging, Clock, DotsThree, CaretDown, CaretUp, Plus, X } from "@phosphor-icons/react";

const SNOOZE = [
  { key: "15m", label: "15 min" },
  { key: "1h", label: "1 hour" },
  { key: "tomorrow", label: "Tomorrow 8am" },
  { key: "dismiss", label: "Dismiss" },
];

const inputCls = "w-full h-10 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const blank = () => ({ title: "", reminder_date: new Date().toISOString().slice(0, 10), reminder_time: "08:00" });

export default function RemindersWidget({ onOpenTask, collapsed, onToggleCollapse }) {
  const [reminders, setReminders] = useState([]);
  const [menuId, setMenuId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/reminders"); setReminders(data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const snooze = async (id, option) => {
    setMenuId(null);
    await api.post(`/reminders/${id}/snooze`, { option });
    toast.success(option === "dismiss" ? "Reminder dismissed" : "Reminder snoozed");
    load();
  };

  const saveReminder = async () => {
    if (!form.title.trim()) { toast.error("Reminder title is required"); return; }
    if (!form.reminder_date) { toast.error("Pick a date"); return; }
    setSaving(true);
    try {
      await api.post("/reminders", { title: form.title, reminder_date: form.reminder_date, reminder_time: form.reminder_time || "08:00" });
      toast.success("Reminder added");
      setForm(blank()); setAdding(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="reminders-widget" className="bg-[#F3E8FF] border-2 border-black rounded-xl hard-shadow-sm p-4 mb-6">
      <div className={`flex items-center justify-between ${collapsed ? "" : "mb-3"}`}>
        <div className="flex items-center gap-2"><BellRinging size={20} weight="fill" /><h3 className="font-display font-bold">Reminders</h3></div>
        <div className="flex items-center gap-2">
          <button data-testid="add-reminder-btn" onClick={() => { setAdding((v) => !v); setForm(blank()); }} title="Add reminder" className="press inline-flex items-center gap-1 text-xs font-bold px-3 h-8 rounded-full border-2 border-black bg-white">
            {adding ? <X size={14} weight="bold" /> : <Plus size={14} weight="bold" />} {adding ? "Close" : "Add"}
          </button>
          {onToggleCollapse && (
            <button data-testid="collapse-reminders" onClick={onToggleCollapse} title={collapsed ? "Expand" : "Collapse"} className="press w-8 h-8 rounded-full border-2 border-black bg-white flex items-center justify-center shrink-0">
              {collapsed ? <CaretDown size={15} weight="bold" /> : <CaretUp size={15} weight="bold" />}
            </button>
          )}
        </div>
      </div>

      {!collapsed && adding && (
        <div data-testid="add-reminder-form" className="bg-white border-2 border-black rounded-lg p-3 mb-3 space-y-2">
          <input data-testid="reminder-title-input" className={inputCls} value={form.title} placeholder="Remind me to…" onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="reminder-date-input" type="date" className={inputCls} value={form.reminder_date} onChange={(e) => setForm((f) => ({ ...f, reminder_date: e.target.value }))} />
            <input data-testid="reminder-time-input" type="time" className={inputCls} value={form.reminder_time} onChange={(e) => setForm((f) => ({ ...f, reminder_time: e.target.value }))} />
          </div>
          <button data-testid="save-reminder-btn" onClick={saveReminder} disabled={saving} className="press w-full h-10 rounded-full bg-primary text-white font-bold border-2 border-black disabled:opacity-60">{saving ? "Saving…" : "Add reminder"}</button>
        </div>
      )}

      {!collapsed && (
      <div className="space-y-2">
        {reminders.length === 0 && !adding && <p className="text-sm text-black/50">No reminders yet. Tap “Add” to set one.</p>}
        {reminders.map((r) => (
          <div key={r.id} data-testid={`reminder-${r.id}`} className="relative flex items-center gap-3 bg-white border-2 border-black rounded-lg p-3">
            <div className="min-w-0 flex-1 cursor-pointer" onClick={() => r.task_id && onOpenTask?.(r.task_id)}>
              <p className="font-bold text-sm leading-tight truncate">{r.title}</p>
              <p className="text-xs text-black/50 flex items-center gap-1"><Clock size={12} weight="bold" />{r.reminder_date} · {r.reminder_time}{r.status === "sent" ? " · sent" : ""}</p>
            </div>
            <button data-testid={`reminder-menu-${r.id}`} onClick={() => setMenuId(menuId === r.id ? null : r.id)} className="press w-8 h-8 rounded-full border-2 border-black flex items-center justify-center shrink-0"><DotsThree size={18} weight="bold" /></button>
            {menuId === r.id && (
              <div className="absolute right-0 top-11 z-20 bg-white border-2 border-black rounded-lg hard-shadow-sm overflow-hidden">
                {SNOOZE.map((s) => (
                  <button key={s.key} data-testid={`snooze-${s.key}-${r.id}`} onClick={() => snooze(r.id, s.key)} className="block w-full text-left px-4 py-2 text-sm font-bold hover:bg-[#FEF9C3] border-b border-black/10">{s.label}</button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
